import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Download } from "lucide-react";
import { Layout } from "../components/tag/Layout";
import { api } from "../lib/api";

export default function ShopConfirmed() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [email, setEmail] = useState("");
  const [downloadUrl, setDownloadUrl] = useState("");
  const [productKey, setProductKey] = useState("");

  useEffect(() => {
    if (!sessionId) { setStatus("paid"); return; }
    let attempts = 0;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.email) setEmail(data.email);
        if (data.product) setProductKey(data.product);
        if (data.download_url) setDownloadUrl(data.download_url);
        if (data.payment_status === "paid") {
          setStatus("paid");
        } else if (data.status === "expired") {
          setStatus("expired");
        } else if (attempts < 10) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          setStatus("timeout");
        }
      } catch {
        if (cancelled) return;
        if (attempts < 5) { attempts++; setTimeout(poll, 2000); }
        else setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <Layout title="Purchase Confirmed | The Authority Gap" path="/shop/confirmed">
      <section style={{ background: "#090909", padding: "80px 32px", textAlign: "center" }}>
        <div className="tag-card" data-testid="shop-confirmed-card" style={{ maxWidth: 560, margin: "0 auto", padding: 32 }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 18 }}>
            <CheckCircle2 size={48} color="#FF6575" />
          </div>
          {status === "paid" && (
            <>
              <h2 className="tag-h2" style={{ marginBottom: 12 }}>Your PDF is ready.</h2>
              <p style={{ color: "#bbb", fontSize: 13, lineHeight: 1.7, marginBottom: 24 }}>
                We also emailed your download link{email ? ` to ${email}` : ""}. The link is valid for 30 days.
              </p>
              {downloadUrl ? (
                <a
                  data-testid="shop-confirmed-download"
                  href={downloadUrl}
                  className="btn-primary"
                  style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 12 }}
                >
                  <Download size={16} /> Download your PDF
                </a>
              ) : (
                <div style={{ color: "#aaa", fontSize: 12, marginBottom: 12 }}>
                  Your link is being prepared — check your inbox in a minute.
                </div>
              )}
              <div>
                <Link to="/shop" data-testid="shop-confirmed-back" style={{ color: "#777", fontSize: 12 }}>
                  ← Back to the shop
                </Link>
              </div>
            </>
          )}
          {status === "checking" && (
            <>
              <h2 className="tag-h2" style={{ marginBottom: 12 }}>Confirming payment…</h2>
              <p style={{ color: "#bbb" }}>Hold tight for a moment.</p>
            </>
          )}
          {(status === "timeout" || status === "error") && (
            <>
              <h2 className="tag-h2" style={{ marginBottom: 12 }}>Still verifying…</h2>
              <p style={{ color: "#bbb", fontSize: 13 }}>
                Your payment may still be processing. If you don't get a confirmation email within a few minutes, contact support@theauthoritygap.co.
              </p>
            </>
          )}
          {status === "expired" && (
            <>
              <h2 className="tag-h2" style={{ marginBottom: 12 }}>Payment session expired</h2>
              <Link to="/shop" className="btn-primary">Back to the shop</Link>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}
